import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type AccessToken, type Passkey } from "../lib/api.js";
import { inZone } from "../lib/dates.js";
import { addPasskey, passkeysSupported, wasCancelled } from "../lib/passkey.js";
import { useSession } from "../lib/session.js";
import { foregroundOn, initialOf } from "../tokens/palette.js";

/**
 * People and colours, the household, and the two links that let Frankie leave
 * the browser: the kitchen screen and the subscribable feeds.
 *
 * The Skylight import used to have a section here too. The move happened once
 * and worked, and a one-time migration offered forever on the settings screen
 * is an invitation to run it a second time. The screen itself is untouched and
 * still answers at /settings/import — including its "remove imported events"
 * undo — so putting the section back is a link, not a rebuild.
 */
export function Settings(): React.JSX.Element {
  const { me, people, zone, signOut, canEdit } = useSession();

  const [tokens, setTokens] = useState<AccessToken[] | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setTokens(await api.tokens());
  }, []);

  const loadPasskeys = useCallback(async () => {
    setPasskeys(await api.passkeys());
  }, []);

  useEffect(() => {
    void loadPasskeys();
  }, [loadPasskeys]);

  async function add(): Promise<void> {
    setAdding(true);
    setPasskeyError(null);
    try {
      await addPasskey();
      await loadPasskeys();
    } catch (error) {
      if (!wasCancelled(error)) {
        setPasskeyError(error instanceof ApiError ? error.message : "That didn't work. Try again.");
      }
    }
    setAdding(false);
  }

  async function remove(id: string): Promise<void> {
    await api.deletePasskey(id);
    await loadPasskeys();
  }

  useEffect(() => {
    void load();
  }, [load]);

  const display = tokens?.find((t) => t.kind === "display");
  const householdFeed = tokens?.find((t) => t.kind === "ics" && t.person_id === null);
  const feedFor = (personId: string): string | null =>
    tokens?.find((t) => t.kind === "ics" && t.person_id === personId)?.token ?? null;

  /*
   * webcal:// is what makes iOS offer to subscribe rather than download a
   * one-off copy of the file.
   */
  const feedUrl = (token: string): string =>
    `webcal://${window.location.host}/feed/${encodeURIComponent(token)}.ics`;

  async function createFeed(personId: string): Promise<void> {
    await api.personFeed(personId);
    await load();
  }
  const displayUrl =
    display?.token == null
      ? null
      : `${window.location.origin}/display?token=${encodeURIComponent(display.token)}`;

  async function copy(label: string, text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access can be refused; the link is on screen either way.
      return;
    }
    setCopied(label);
    setTimeout(() => setCopied(null), 1800);
  }

  async function rotate(): Promise<void> {
    setRotating(true);
    await api.rotateToken("display");
    await load();
    setRotating(false);
  }

  return (
    <div className="screen">
      <header className="week__head week__head--plain">
        <h1>Settings</h1>
      </header>

      <section className="section">
        <p className="label">People</p>
        {people.map((person) => (
          <div key={person.id} className="person-row">
            <span
              className="disc"
              style={{
                width: 34,
                height: 34,
                background: person.color,
                color: foregroundOn(person.color),
                fontSize: 15,
              }}
            >
              {initialOf(person.display_name)}
            </span>
            <span className="person-row__name">{person.display_name}</span>
            <span className="person-row__role">{person.role ?? ""}</span>
            <span className="person-row__hex">{person.color}</span>
          </div>
        ))}
      </section>

      <section className="section">
        <p className="label">Household</p>
        <div className="kv">
          <span>Time zone</span>
          <span className="kv__value">{zone}</span>
        </div>
        <div className="kv">
          <span>Week starts</span>
          <span className="kv__value">Sunday</span>
        </div>
        <div className="kv">
          <span>Signed in as</span>
          <span className="kv__value">{me.user.email}</span>
        </div>
      </section>

      <section className="section">
        <p className="label">Sign-in</p>
        <p className="note">
          Face ID, Touch ID or your phone&rsquo;s screen lock, instead of waiting for an email.
          The email link always works too &mdash; it is how a new phone gets in.
        </p>

        {passkeys?.map((passkey) => (
          <div key={passkey.id} className="kv kv--action">
            <span>{passkey.label}</span>
            <span className="kv__value">
              added {inZone(passkey.created_at, zone).toFormat("d LLL yyyy")}
              <button
                type="button"
                className="danger danger--inline passkey__remove"
                onClick={() => void remove(passkey.id)}
              >
                Remove
              </button>
            </span>
          </div>
        ))}

        {passkeysSupported() ? (
          <div className="buttons">
            <button
              type="button"
              className="button-quiet"
              disabled={adding}
              onClick={() => void add()}
            >
              {adding ? "Waiting…" : "Add Face ID on this device"}
            </button>
          </div>
        ) : (
          <p className="note note--pending">This browser can&rsquo;t hold a passkey.</p>
        )}
        {passkeyError !== null && <p className="error">{passkeyError}</p>}
      </section>

      <section className="section">
        <p className="label">Kitchen screen</p>
        <p className="note">
          Open this on the screen in the kitchen. Treat it as a secret — anyone holding it can
          read the family&rsquo;s schedule.
        </p>

        {displayUrl === null ? (
          <p className="note note--pending">No link yet.</p>
        ) : (
          <p className="linkbox">{displayUrl}</p>
        )}

        <div className="buttons">
          {displayUrl !== null && (
            <button
              type="button"
              className="button-quiet"
              onClick={() => void copy("link", displayUrl)}
            >
              {copied === "link" ? "Copied" : "Copy link"}
            </button>
          )}
          {/* Handing out a link to the household's schedule is a change to
              the household, so it follows the same rule as everything else. */}
          {canEdit && (
            <button
              type="button"
              className="danger danger--inline"
              disabled={rotating}
              onClick={() => void rotate()}
            >
              {rotating ? "Making one…" : "Revoke and make a new one"}
            </button>
          )}
        </div>

        <p className="note">
          On an iPad, open the link and use Share &rarr; Add to Home Screen. The icon comes back
          to this screen, in landscape. An icon added before this worked opened the phone app
          instead &mdash; delete that one and add it again.
        </p>

        <p className="note note--pending">
          A new link stops the old one working. The screen needs the new one, and so does the
          home-screen icon.
        </p>
      </section>

      <section className="section">
        <p className="label">Subscribe on your phone</p>
        <p className="note">
          Read-only. Edits happen here, and iOS gives you the reminders. Expect changes to take
          15 to 60 minutes to appear — that is iOS deciding when to check, not Frankie.
        </p>

        {householdFeed?.token != null && (
          <div className="feed">
            <span className="feed__who">Everyone</span>
            <span className="linkbox linkbox--inline">{feedUrl(householdFeed.token)}</span>
            <button
              type="button"
              className="button-quiet"
              onClick={() => void copy("everyone", feedUrl(householdFeed.token ?? ""))}
            >
              {copied === "everyone" ? "Copied" : "Copy"}
            </button>
          </div>
        )}

        {people.map((person) => {
          const token = feedFor(person.id);
          return (
            <div key={person.id} className="feed">
              <span className="feed__who">{person.display_name}</span>
              {token === null ? (
                canEdit ? (
                  <button
                    type="button"
                    className="button-quiet feed__make"
                    onClick={() => void createFeed(person.id)}
                  >
                    Make a link
                  </button>
                ) : (
                  <span className="note note--pending">No link yet</span>
                )
              ) : (
                <>
                  <span className="linkbox linkbox--inline">{feedUrl(token)}</span>
                  <button
                    type="button"
                    className="button-quiet"
                    onClick={() => void copy(person.display_name, feedUrl(token))}
                  >
                    {copied === person.display_name ? "Copied" : "Copy"}
                  </button>
                </>
              )}
            </div>
          );
        })}

        <p className="note note--pending">
          Each link is a secret — anyone holding one can read what it covers.
        </p>
      </section>

      <button type="button" className="danger" onClick={() => void signOut()}>
        Log out
      </button>
    </div>
  );
}
